import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import WikiMarkdownRenderer, { extractHeadings } from '../../components/WikiMarkdownRenderer'

interface WikiArticle {
  title: string
  slug: string
  content: string
  conclusions: number
  lastCompiled: string
  contributors?: string[]
}

export default function WikiArticlePage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()

  const { data: article, isLoading, error } = useQuery({
    queryKey: ['wikiArticle', slug],
    queryFn: async (): Promise<WikiArticle> => {
      const res = await fetch(`/api/wiki/${encodeURIComponent(slug!)}`)
      if (!res.ok) throw new Error('Article not found')
      return res.json()
    },
    enabled: !!slug,
  })

  const headings = article ? extractHeadings(article.content) : []
  // Only show TOC if there are enough headings to make it useful
  const showToc = headings.filter(h => h.level <= 2).length >= 3

  function handleBacklinkClick(targetSlug: string) {
    navigate(`/wiki/${encodeURIComponent(targetSlug)}`)
  }

  // Extract related topics from [[backlinks]] in the Related Topics section
  const relatedLinks = article
    ? extractRelatedLinks(article.content)
    : []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="wiki-loading">
          <div className="wiki-loading-dot" />
          <div className="wiki-loading-dot" style={{ animationDelay: '150ms' }} />
          <div className="wiki-loading-dot" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    )
  }

  if (error || !article) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <div style={{ fontSize: 32, opacity: 0.3 }}>📄</div>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Article not found</p>
        <Link to="/wiki" className="text-xs no-underline" style={{ color: 'var(--accent)' }}>
          ← Back to Wiki
        </Link>
      </div>
    )
  }

  return (
    <div className="wiki-article-layout">
      {/* Table of Contents sidebar */}
      {showToc && (
        <aside className="wiki-toc">
          <div className="wiki-toc-header">Contents</div>
          <nav>
            {headings.filter(h => h.level <= 3).map((h, i) => (
              <a
                key={i}
                href={`#${h.id}`}
                className="wiki-toc-item"
                style={{ paddingLeft: `${(h.level - 1) * 12 + 8}px` }}
                onClick={(e) => {
                  e.preventDefault()
                  document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
              >
                {h.text}
              </a>
            ))}
          </nav>
        </aside>
      )}

      {/* Main article content */}
      <article className={`wiki-article ${showToc ? 'wiki-article-with-toc' : ''}`}>
        {/* Breadcrumb */}
        <div className="wiki-breadcrumb">
          <Link to="/wiki" className="wiki-breadcrumb-link">Wiki</Link>
          <span className="wiki-breadcrumb-sep">/</span>
          <span className="wiki-breadcrumb-current">{article.title}</span>
        </div>

        {/* Article header */}
        <header className="wiki-article-header">
          <h1 className="wiki-article-title">{article.title}</h1>
          <div className="wiki-article-meta">
            <span className="wiki-meta-item" title="Conclusions synthesized">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
              </svg>
              {article.conclusions} conclusions
            </span>
            <span className="wiki-meta-sep">·</span>
            <span className="wiki-meta-item" title="Last compiled">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
              {formatDate(article.lastCompiled)}
            </span>
            {article.contributors && article.contributors.length > 0 && (
              <>
                <span className="wiki-meta-sep">·</span>
                <span className="wiki-meta-item" title="Contributors">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  {article.contributors.join(', ')}
                </span>
              </>
            )}
          </div>
        </header>

        {/* Article body */}
        <div className="wiki-article-body" onClick={(e) => {
          // Intercept wiki backlink clicks for SPA navigation
          const target = e.target as HTMLElement
          const link = target.closest('a.wiki-backlink') as HTMLAnchorElement
          if (link) {
            e.preventDefault()
            const href = link.getAttribute('href')
            if (href) navigate(href)
          }
        }}>
          <WikiMarkdownRenderer
            content={article.content}
            onBacklinkClick={handleBacklinkClick}
          />
        </div>

        {/* Related articles */}
        {relatedLinks.length > 0 && (
          <footer className="wiki-related">
            <h3 className="wiki-related-title">Related Articles</h3>
            <div className="wiki-related-grid">
              {relatedLinks.map((link, i) => (
                <Link
                  key={i}
                  to={`/wiki/${encodeURIComponent(link.slug)}`}
                  className="wiki-related-card"
                >
                  <span className="wiki-related-card-title">{link.title}</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.4, flexShrink: 0 }}>
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </Link>
              ))}
            </div>
          </footer>
        )}
      </article>
    </div>
  )
}

function extractRelatedLinks(content: string): { title: string; slug: string }[] {
  // Look for [[links]] in the last section (usually "Related Topics")
  const lines = content.split('\n')
  const relatedIdx = lines.findIndex(l => l.match(/^#+\s+Related/i))
  const section = relatedIdx >= 0 ? lines.slice(relatedIdx).join('\n') : ''

  const links: { title: string; slug: string }[] = []
  const re = /\[\[(.+?)\]\]/g
  let match
  while ((match = re.exec(section)) !== null) {
    const title = match[1]
    links.push({ title, slug: title.replace(/\s+/g, '-') })
  }
  return links
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr + 'T00:00:00')
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return dateStr
  }
}
